
import dayjs from "dayjs";
import APIService from "../API";
import { AppStorage } from "../Storage";
import {
    RefreshTokenDtoType,
    LoginDtoResponseType,
    ResetPasswordDtoType,
    ResetPasswordResponseDtoType
} from "@mindmom/common";

interface LoginDtoResponseTypeWithTimestamps extends LoginDtoResponseType {
    refreshTokenEpiresAtDate: string;
    accessTokenExpiresAtDate: string;
}

export class AuthService {
    static StorageKey: string = "authData";

    private static IsRefreshing: boolean = false;

    static async refreshAccessToken(): Promise<string> {
        this.IsRefreshing = true;
        const authData = await APIService.handlePublicPostRequest<
            RefreshTokenDtoType,
            LoginDtoResponseType
        // eslint-disable-next-line camelcase
        >({ refresh_token: this.getRefreshToken() }, "/auth/refresh_token");
        this.saveAuthDataToLocalStorage(authData);
        this.IsRefreshing = false;
        return authData.access_token;
    }

    static getRefreshToken(): string {
        const result: string | null = AppStorage.getItem(this.StorageKey);
        if (!result) {
            throw Error("Tried to get refresh token but no data on storage");
        }
        const dataWithTimestamps = JSON.parse(
            result
        ) as LoginDtoResponseTypeWithTimestamps;
        return dataWithTimestamps.refresh_token;
    }

    static getAccessToken(): string {
        const appAuthData = AppStorage.getItem(this.StorageKey);
        if (appAuthData) {
            const dataWithTimestamps = JSON.parse(
                appAuthData
            ) as LoginDtoResponseTypeWithTimestamps;
            return dataWithTimestamps.access_token;
        }
        throw new Error("Tried to get access token but no data on storage ");
    }

    static isRefreshTokenExpired(): boolean {
        const result: string | null = AppStorage.getItem(this.StorageKey);
        if (!result) {
            return false;
        }
        const dataWithTimestamps = JSON.parse(
            result
        ) as LoginDtoResponseTypeWithTimestamps;
        return dayjs().isAfter(dataWithTimestamps.refreshTokenEpiresAtDate);
    }

    static isAccessTokenExpired(): boolean {
        const result: string | null = AppStorage.getItem(this.StorageKey);
        if (!result) {
            return false;
        }
        const dataWithTimestamps = JSON.parse(
            result
        ) as LoginDtoResponseTypeWithTimestamps;
        return dayjs().isAfter(dataWithTimestamps.accessTokenExpiresAtDate);
    }

    static logout() {
        AppStorage.removeItem(this.StorageKey);
    }

    static saveAuthDataToLocalStorage(data: LoginDtoResponseType): void {
        const dataWithTimestamps: LoginDtoResponseTypeWithTimestamps = {
            ...data,
            refreshTokenEpiresAtDate: dayjs()
                .add(data.refresh_expires_in, "s")
                .toISOString(),
            accessTokenExpiresAtDate: dayjs()
                .add(data.expires_in, "s")
                .toISOString(),
        };

        AppStorage.saveItem("userAuthData", JSON.stringify(dataWithTimestamps));
    }

    static sendResetPassword = (email: string) =>
        APIService.handlePublicPostRequest<
            ResetPasswordDtoType,
            ResetPasswordResponseDtoType
        >({ email }, "/auth/send-reset-password");
}
